import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactI18n from 'react-i18n';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import {
  Box,
  FlexBox,
  TextBody,
  TextCaption,
  TextDense,
  TextH2,
  CallToActionLink,
  Panel,
  Pill,
  Spacer,
  IconFontAwesome
} from '@directsoftware/ui-kit-web-admin';
import { formatCurrency } from '../../../../shared/helpers';
import { requirementPresentation } from '../../my-stay/requirement-presentation';
import { REQUIREMENT, STATUS } from '../../../utils/guest-stay-state';
import {
  receiptPlaceholders,
  depositCharge,
  depositAmount,
  cancellationPolicy,
  checklistSummary,
  depositStatus,
  checklistItems,
  showChecklistCard
} from '../../../utils/receipt-redesign';
import ShowMoreWrapper from '../../listing/shared-v2/show-more-wrapper';
import useIsPrinting from './use-is-printing';
import {
  fetchWlsAuthConfig,
  reportWlsAuthConfigError
} from '../../../utils/wls-api-client';

const t = (key, values) =>
  ReactI18n.getIntlMessage(`cx.receipt.redesign.${key}`, values);
const date = value => {
  const parsed = moment.parseZone(
    value,
    [moment.ISO_8601, 'MM/DD/YYYY HH:mm:ss'],
    true
  );
  return value && parsed.isValid() ? parsed.format('MMM D, YYYY') : '';
};
const Progress = ({ value, max, label, valueText }) => (
  <div
    className="receiptRedesign__progress"
    role="progressbar"
    aria-label={label}
    aria-valuemin="0"
    aria-valuemax="100"
    aria-valuenow={
      max ? Math.round(Math.max(0, Math.min(100, (value / max) * 100))) : 0
    }
    aria-valuetext={valueText}
  >
    <span
      style={{
        width: `${max ? Math.max(0, Math.min(100, (value / max) * 100)) : 0}%`
      }}
    />
  </div>
);

const depositDescription = (receipt, currency, depositState, isResolving) => {
  const charge = depositCharge(receipt.charges);
  const status = depositStatus(receipt, depositState, isResolving);
  const rawAmount = depositAmount(receipt, depositState);
  const amount = rawAmount == null ? null : formatCurrency(rawAmount, currency);
  if (charge && ['authorized', 'charged'].includes(status)) {
    const when = date(charge.created_at);
    let message = when ? 'deposit_charged_date' : 'deposit_charged';
    if (charge.status === 'auth')
      message = when ? 'hold_placed_date' : 'hold_placed';
    if (amount == null)
      message =
        charge.status === 'auth'
          ? 'hold_placed_no_amount'
          : 'deposit_charged_no_amount';
    return {
      status,
      description: t(message, { amount, date: when })
    };
  }
  if (status === 'collected')
    return {
      status,
      description: t('deposit_collected_summary')
    };
  if (status !== 'scheduled')
    return {
      status,
      description: ['action_needed', 'complete'].includes(status)
        ? ''
        : t('deposit_status_unavailable')
    };
  const scheduledOn = date(
    receiptPlaceholders(receipt).scheduledOn ||
      (depositState && depositState.scheduledDate)
  );
  let scheduledMessage = scheduledOn ? 'hold_scheduled_date' : 'hold_scheduled';
  if (amount == null) scheduledMessage = 'hold_scheduled_no_amount';
  return {
    status: 'scheduled',
    description: t(scheduledMessage, { amount, date: scheduledOn })
  };
};

const ChecklistCard = ({ receipt, stayState, currency, brand }) => {
  const [authenticateEnabled, setAuthenticateEnabled] = useState(false);
  useEffect(() => {
    fetchWlsAuthConfig(brand)
      .then(res =>
        setAuthenticateEnabled(
          Boolean(
            res.data.authenticate_enabled && res.data.medallion_workflow_id
          )
        )
      )
      .catch(reportWlsAuthConfigError);
  }, []);
  if (!showChecklistCard(stayState)) return null;
  if (stayState.lifecycle === 'in_progress')
    return (
      <Panel>
        <Box padding="m">
          <TextH2 renderAs="h2" isFullWidth>
            {t('enjoy_stay')}
          </TextH2>
          <TextBody isFullWidth>{t('enjoy_stay_description')}</TextBody>
        </Box>
      </Panel>
    );
  const { requirements } = stayState;
  const authenticate =
    authenticateEnabled &&
    !brand.isRvFleet &&
    !receipt.mvr_verification_attempted &&
    stayState.actionable;
  const items = checklistItems(stayState, authenticate);
  const summary = checklistSummary(items);
  const instruction = requirements[REQUIREMENT.checkInInstructions];
  return (
    <Panel>
      <Box padding="m">
        <section aria-labelledby="before-heading">
          <TextCaption
            className="receiptRedesign__eyebrow"
            textColor="light-gray"
            textTransform="uppercase"
            isFullWidth
          >
            {t('before_arrival')}
          </TextCaption>
          <FlexBox justifyContent="space-between" alignItems="center" gap="s">
            <TextH2 renderAs="h2" id="before-heading">
              {t(summary.heading, { count: summary.actions })}
            </TextH2>
            {summary.allSet && <Pill color="green">{t('all_set')}</Pill>}
          </FlexBox>
          <Spacer size="xs" />
          <ul className="receiptRedesign__checklist">
            {items.map(item => {
              const isDeposit = item.key === REQUIREMENT.securityDeposit;
              const deposit =
                isDeposit &&
                [STATUS.complete, STATUS.scheduled].includes(item.status)
                  ? depositDescription(receipt, currency, item)
                  : null;
              let status = 'pending';
              if (item.status === STATUS.scheduled) status = 'scheduled';
              if (item.guestActionRequired) status = 'action_needed';
              if (item.status === STATUS.complete) status = 'complete';
              if (deposit) ({ status } = deposit);
              let color = 'gray';
              if (status === 'action_needed') color = 'yellow';
              if (
                ['complete', 'authorized', 'charged', 'collected'].includes(
                  status
                )
              )
                color = 'green';
              const name =
                item.key === REQUIREMENT.securityDeposit
                  ? 'security_deposit'
                  : item.key;
              const done = [
                'complete',
                'authorized',
                'charged',
                'collected'
              ].includes(status);
              let icon = null;
              if (done) icon = 'check';
              else if (status === 'action_needed') icon = 'exclamationTriangle';
              else if (status === 'scheduled') icon = 'clock';
              else if (item.status === STATUS.dueLater) icon = 'calendarAlt';
              return (
                <li key={item.key}>
                  <FlexBox className="receiptRedesign__check" gap="s">
                    <span
                      className={`receiptRedesign__checkIcon${
                        done ? ' receiptRedesign__checkIcon--done' : ''
                      }`}
                    >
                      {icon ? (
                        <IconFontAwesome name={icon} iconStyle="regular" />
                      ) : (
                        <TextBody renderAs="span">?</TextBody>
                      )}
                    </span>
                    <Box flex="1" className="receiptRedesign__grow">
                      <div className="receiptRedesign__checkBody">
                        <div className="receiptRedesign__checkDetails">
                          <FlexBox alignItems="center" flexWrap="wrap" gap="s">
                            <TextBody renderAs="h3" weight="semibold">
                              {t(`requirement_${name}`)}
                            </TextBody>
                            <Pill color={color}>{t(`status_${status}`)}</Pill>
                          </FlexBox>
                          <Spacer size="xxs" />
                          {(item.parts || [item]).map(part => {
                            const partPresentation = requirementPresentation(
                              part
                            );
                            let partStatus = 'pending';
                            if (part.status === STATUS.complete)
                              partStatus = 'complete';
                            if (part.guestActionRequired)
                              partStatus = 'action_needed';
                            const rawDate = part.scheduledDate || part.dueDate;
                            const dateValue =
                              rawDate && moment(rawDate, 'YYYY-MM-DD', true);
                            const meta =
                              partPresentation.metaKey &&
                              ReactI18n.getIntlMessage(
                                partPresentation.metaKey,
                                {
                                  amount: part.amount
                                    ? formatCurrency(
                                        Number(part.amount).toFixed(2),
                                        currency
                                      )
                                    : null,
                                  date:
                                    dateValue && dateValue.isValid()
                                      ? dateValue.format('MMMM D, YYYY')
                                      : null,
                                  card:
                                    part.paymentMethod &&
                                    part.paymentMethod.last_four
                                      ? `${part.paymentMethod.brand || ''} ••${
                                          part.paymentMethod.last_four
                                        }`.trim()
                                      : null
                                }
                              );
                            return (
                              <Box key={part.key}>
                                <FlexBox
                                  alignItems="center"
                                  flexWrap="wrap"
                                  gap="xs"
                                >
                                  <TextBody>
                                    {deposit
                                      ? deposit.description
                                      : ReactI18n.getIntlMessage(
                                          partPresentation.translationKey
                                        )}
                                  </TextBody>
                                  {item.key === REQUIREMENT.verification && (
                                    <span className="receiptRedesign__visuallyHidden">
                                      {` (${t(`status_${partStatus}`)})`}
                                    </span>
                                  )}
                                </FlexBox>
                                {meta && !deposit && (
                                  <TextBody renderAs="p">{meta}</TextBody>
                                )}
                              </Box>
                            );
                          })}
                        </div>
                        <div className="receiptRedesign__checkActions">
                          {(item.parts || [item])
                            .filter(
                              part => part.guestActionRequired && part.route
                            )
                            .map(part => (
                              <CallToActionLink
                                key={part.key}
                                href={part.route}
                                variation="secondary"
                                appearance="outline"
                                size="dense"
                              >
                                {t(`action_${part.key}`)}
                              </CallToActionLink>
                            ))}
                          {item.key === REQUIREMENT.verification &&
                            authenticate && (
                              <CallToActionLink
                                href={`https://verify.authenticating.com/?token=${
                                  receipt.medallion_token
                                }`}
                                type="link"
                                variation="secondary"
                                appearance="outline"
                                size="dense"
                                openNewTab
                              >
                                {t('complete_verification')}
                              </CallToActionLink>
                            )}
                        </div>
                      </div>
                    </Box>
                  </FlexBox>
                </li>
              );
            })}
          </ul>
          {instruction && instruction.status !== STATUS.unavailable && (
            <Box padding="s" className="receiptRedesign__note">
              <FlexBox
                justifyContent="space-between"
                alignItems="center"
                gap="s"
              >
                <span
                  aria-hidden="true"
                  className={`receiptRedesign__checkIcon${
                    instruction.status === STATUS.complete
                      ? ' receiptRedesign__checkIcon--done'
                      : ''
                  }`}
                >
                  <IconFontAwesome name="envelope" iconStyle="regular" />
                </span>
                <Box flex="1" className="receiptRedesign__grow">
                  <TextBody renderAs="h3" weight="semibold" isFullWidth>
                    {t(
                      instruction.status === STATUS.complete
                        ? 'instructions_sent_title'
                        : 'instructions_title'
                    )}
                  </TextBody>
                  <TextDense renderAs="p" isFullWidth>
                    {t(
                      instruction.status === STATUS.complete
                        ? 'instructions_sent'
                        : 'instructions_pending'
                    )}
                  </TextDense>
                </Box>
                <Pill
                  color={
                    instruction.status === STATUS.complete ? 'green' : 'gray'
                  }
                >
                  {t(
                    instruction.status === STATUS.complete
                      ? 'instructions_sent_pill'
                      : 'instructions_pending_pill'
                  )}
                </Pill>
              </FlexBox>
            </Box>
          )}
        </section>
      </Box>
    </Panel>
  );
};

const DetailsCard = ({ receipt }) => {
  const [open, setOpen] = useState(false);
  const isPrinting = useIsPrinting();
  const booking = receipt.booking || {};
  const manager = receipt.property_manager || {};
  const { cancellation } = receiptPlaceholders(receipt);
  const policy = cancellationPolicy(receipt);
  const eligibilityText =
    cancellation &&
    t(`eligibility_${cancellation.status}`, { date: cancellation.date });
  return (
    <Panel>
      <Box padding="m">
        <section aria-labelledby="details-heading">
          <TextH2 renderAs="h2" id="details-heading">
            {t('reservation_information')}
          </TextH2>
          <dl className="receiptRedesign__detailsList">
            <div>
              <TextDense renderAs="dt" isFullWidth>
                {t('reserved_for')}
              </TextDense>
              <TextBody renderAs="dd" weight="semibold" isFullWidth>
                {(receipt.customer || {}).name || '—'}
              </TextBody>
            </div>
            <div>
              <TextDense renderAs="dt" isFullWidth>
                {t('booking_code')}
              </TextDense>
              <TextBody renderAs="dd" weight="semibold" isFullWidth>
                {booking.booking_code || '—'}
              </TextBody>
            </div>
          </dl>
          {(eligibilityText || policy.refund_policy != null) && (
            <Box className="receiptRedesign__policy">
              <FlexBox gap="s" alignItems="flex-start">
                <span
                  className="receiptRedesign__detailsIcon"
                  aria-hidden="true"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M12 2 4 5v6c0 5 3.3 8.8 8 11 4.7-2.2 8-6 8-11V5l-8-3Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </span>
                <Box flex="1" className="receiptRedesign__grow">
                  <TextBody renderAs="h3" isFullWidth weight="semibold">
                    {eligibilityText ||
                      ReactI18n.getIntlMessage(
                        `global.refund_policy.${policy.refund_policy}.label`
                      )}
                  </TextBody>
                  <TextBody isFullWidth>
                    {t('cancellation_explanation')}
                  </TextBody>
                  {policy.refund_policy != null && (
                    <>
                      <button
                        type="button"
                        className="receiptRedesign__link"
                        aria-expanded={open}
                        aria-controls="receipt-cancellation-details"
                        onClick={() => setOpen(!open)}
                      >
                        <TextBody weight="semibold">
                          {t('read_policy')}
                        </TextBody>
                      </button>
                      <div
                        id="receipt-cancellation-details"
                        hidden={!open && !isPrinting}
                      >
                        {(open || isPrinting) && (
                          <>
                            <Spacer size="xs" />
                            <div className="receiptRedesign__breakdown">
                              <ShowMoreWrapper>
                                <TextDense weight="regular">
                                  {policy.refund_policy === 'custom'
                                    ? policy.refund_policy_custom
                                    : ReactI18n.getIntlMessage(
                                        `global.refund_policy.${
                                          policy.refund_policy
                                        }.details`
                                      )}
                                </TextDense>
                              </ShowMoreWrapper>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </Box>
              </FlexBox>
            </Box>
          )}
          {(manager.name || manager.email || manager.telephone) && (
            <Box className="receiptRedesign__policy">
              <FlexBox gap="s" alignItems="flex-start">
                <span
                  className="receiptRedesign__detailsIcon"
                  aria-hidden="true"
                >
                  <IconFontAwesome name="envelope" iconStyle="regular" />
                </span>
                <Box>
                  <TextBody renderAs="h3" isFullWidth weight="semibold">
                    {ReactI18n.getIntlMessage(
                      'cx.global.property_manager_info'
                    )}
                  </TextBody>
                  {manager.name && (
                    <TextBody isFullWidth>{manager.name}</TextBody>
                  )}
                  {manager.email && (
                    <TextBody isFullWidth>
                      {'Email: '}
                      <Link to={`mailTo:${manager.email}`}>
                        {manager.email}
                      </Link>
                    </TextBody>
                  )}
                  {manager.telephone && (
                    <TextBody isFullWidth>
                      {'Phone: '}
                      <Link
                        to={`tel:${manager.telephone.replace(/[^0-9]/, '')}`}
                      >
                        {manager.telephone}
                      </Link>
                    </TextBody>
                  )}
                </Box>
              </FlexBox>
            </Box>
          )}
        </section>
      </Box>
    </Panel>
  );
};

Progress.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  valueText: PropTypes.string
};
Progress.defaultProps = { valueText: null };
ChecklistCard.propTypes = {
  receipt: PropTypes.object.isRequired,
  stayState: PropTypes.object,
  currency: PropTypes.string,
  brand: PropTypes.object.isRequired
};
DetailsCard.propTypes = { receipt: PropTypes.object.isRequired };
ChecklistCard.defaultProps = { stayState: null, currency: null };

export { ChecklistCard, DetailsCard, Progress, depositDescription, date };
